import React, {createContext, PropsWithChildren, useMemo, useContext} from 'react';
import {Skill} from '../models';
import {SkillService, SkillServiceImpl, CreateSkillParams, GetSkillsParams} from '../services';
import {useAuth} from './auth.provider';

export interface SkillProviderInterface {
  skillService: SkillService;
  createSkill: (name: string) => Promise<Skill>;
  getSkills: (params: {page: number; size: number; search?: string}) => Promise<Skill[]>;
}

export const SkillContext = createContext<SkillProviderInterface>(undefined as any as SkillProviderInterface);

export const useSkill = () => {
  const context = useContext(SkillContext);
  if (!context) {
    throw new Error('useSkill must be used within a SkillProvider');
  }
  return context;
};

export const SkillProvider: React.FC<PropsWithChildren<{}>> = ({children}) => {
  const skillService: SkillService = useMemo(() => new SkillServiceImpl(), []);
  const {user} = useAuth();

  const createSkill = async (name: string): Promise<Skill> => {
    if (!user?.id) {
      throw new Error('Account ID not found in context');
    }
    const createParams = new CreateSkillParams({
      createdBy: user.id,
      name,
    });
    const response = await skillService.createSkill(createParams);
    if (response.error) {
      throw new Error(response.error);
    }
    return response.data!;
  };

  const getSkills = async (params: {page: number; size: number; search?: string}): Promise<Skill[]> => {
    const getParams = new GetSkillsParams(params);
    const response = await skillService.getSkills(getParams);
    if (response.error) {
      throw new Error(response.error);
    }
    return response.data!;
  };

  return (
    <SkillContext.Provider
      value={{
        skillService,
        createSkill,
        getSkills,
      }}>
      {children}
    </SkillContext.Provider>
  );
};
